import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/shared/ui/card';
import { Edit, Plus, Trash2 } from 'lucide-react';
import { ReceivableStatusBadge, ReceivableFilters, ReceivableDeleteDialog } from '../components';
import { useReceivables, useDeleteReceivable } from '../hooks';
import { GetReceivablesParams, Receivable, ReceivableStatus } from '../utils/types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { Pagination, DataTable } from '@/shared/widgets';
import { DateRange } from 'react-day-picker';
import { useTranslation } from '@/shared/i18n';

export const ReceivablesList = () => {
  const { t } = useTranslation('receivables');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Estados dos filtros
  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [statusFilter, setStatusFilter] = useState<ReceivableStatus | 'all'>(
    (searchParams.get('status') as ReceivableStatus | 'all') ?? 'all'
  );
  // Função auxiliar para converter ISO string para Date
  const parseDateFromIso = (isoString?: string): Date | undefined => {
    if (!isoString) return undefined;
    try {
      const date = new Date(isoString);
      if (Number.isNaN(date.getTime())) return undefined;
      return date;
    } catch {
      return undefined;
    }
  };

  // Inicializa ranges de data a partir dos query params
  const getInitialDateRange = (startParam?: string, endParam?: string): DateRange | undefined => {
    const from = parseDateFromIso(startParam);
    const to = parseDateFromIso(endParam);
    if (!from && !to) return undefined;
    return { from, to };
  };

  const [dateReferenceRange, setDateReferenceRange] = useState<DateRange | undefined>(
    getInitialDateRange(
      searchParams.get('relatedDateStart') ?? undefined,
      searchParams.get('relatedDateEnd') ?? undefined
    )
  );
  const [paidDateRange, setPaidDateRange] = useState<DateRange | undefined>(
    getInitialDateRange(
      searchParams.get('paidDateStart') ?? undefined,
      searchParams.get('paidDateEnd') ?? undefined
    )
  );
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page') ?? '1') || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('pageSize') ?? '10') || 10);
  const debouncedSearch = useDebounce(search, 400);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [receivableToDelete, setReceivableToDelete] = useState<Receivable | null>(null);

  // Formata data para ISO string com hora e fuso
  // Start: início do dia (00:00:00.000Z)
  // End: fim do dia (23:59:59.999Z)
  const formatDateStartForApi = useCallback((date?: Date): string | undefined => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00.000Z`;
  }, []);

  const formatDateEndForApi = useCallback((date?: Date): string | undefined => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T23:59:59.999Z`;
  }, []);

  // Monta filtros para API
  const filtrosApi: GetReceivablesParams = {
    page: currentPage,
    limit: pageSize,
    status: statusFilter,
    search: debouncedSearch,
    sortBy: 'createdAt',
    order: 'desc',
    relatedDateStart: formatDateStartForApi(dateReferenceRange?.from),
    relatedDateEnd: formatDateEndForApi(dateReferenceRange?.to),
    paidDateStart: formatDateStartForApi(paidDateRange?.from),
    paidDateEnd: formatDateEndForApi(paidDateRange?.to),
  };

  const {
    receivables,
    totalPages,
    totalItems,
    currentPage: currentPageFromApi,
    isLoading,
    error,
    queryResult,
  } = useReceivables(filtrosApi);

  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (dateReferenceRange?.from) params.set('relatedDateStart', formatDateStartForApi(dateReferenceRange.from)!);
    if (dateReferenceRange?.to) params.set('relatedDateEnd', formatDateEndForApi(dateReferenceRange.to)!);
    if (paidDateRange?.from) params.set('paidDateStart', formatDateStartForApi(paidDateRange.from)!);
    if (paidDateRange?.to) params.set('paidDateEnd', formatDateEndForApi(paidDateRange.to)!);
    if (currentPage > 1) params.set('page', String(currentPage));
    if (pageSize !== 10) params.set('pageSize', String(pageSize));
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, statusFilter, dateReferenceRange, paidDateRange, currentPage, pageSize, setSearchParams, formatDateStartForApi, formatDateEndForApi]);
  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, statusFilter, dateReferenceRange, paidDateRange, pageSize]);
  useEffect(() => { if (currentPageFromApi && currentPageFromApi !== currentPage) setCurrentPage(currentPageFromApi); }, [currentPageFromApi, currentPage]);
  useEffect(() => { const apiLimit = queryResult.data?.limit; if (typeof apiLimit === 'number' && apiLimit > 0 && apiLimit !== pageSize) setPageSize(apiLimit); }, [pageSize, queryResult.data?.limit]);
  const { deleteReceivable, isLoading: isDeleting } = useDeleteReceivable();
  const handleEdit = (id: string) => navigate(`/receivables/${id}`);
  const handleDelete = (receivable: Receivable) => { setReceivableToDelete(receivable); setDeleteDialogOpen(true); };
  const confirmDelete = async () => { if (!receivableToDelete) return; try { await deleteReceivable(receivableToDelete.id); setDeleteDialogOpen(false); setReceivableToDelete(null); } catch (error) { console.error('Erro ao excluir recebível:', error); } };
  // Converte string do backend (YYYY-MM-DD ou ISO string) para Date, evitando problemas de timezone
  // Cria a data no timezone local para que seja exibida corretamente
  const parseDateFromBackend = (dateString?: string): Date | undefined => {
    if (!dateString) return undefined;
    
    // Extrai a parte da data (YYYY-MM-DD) de uma ISO string ou usa a string diretamente
    let datePart: string;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // Já está no formato YYYY-MM-DD
      datePart = dateString;
    } else if (/^\d{4}-\d{2}-\d{2}T/.test(dateString)) {
      // É uma ISO string, extrai apenas a parte da data
      datePart = dateString.split('T')[0];
    } else {
      // Formato desconhecido, tenta usar new Date normalmente
      return new Date(dateString);
    }
    
    // Cria a data no timezone local (não UTC) para que seja exibida corretamente
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = parseDateFromBackend(dateString);
    return date ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : '-';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('receivables')}</h1>
          <p className="text-muted-foreground">{t('manageReceivables')}</p>
        </div>
        <Button onClick={() => navigate('/receivables/new')}>
          <Plus className="mr-2 h-4 w-4" /> {t('newReceivable')}
        </Button>
      </div>

      {/* Filtros */}
      <Card className='bg-white/[0.03] border-white/[0.06]'>
        <CardHeader>
          <CardTitle>{t('filters')}</CardTitle>
          <CardDescription>{t('filterByStatusDate')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ReceivableFilters
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            dateReferenceRange={dateReferenceRange}
            onDateReferenceRangeChange={setDateReferenceRange}
            paidDateRange={paidDateRange}
            onPaidDateRangeChange={setPaidDateRange}
          />
        </CardContent>
      </Card>

      {/* Tabela */}
      <DataTable
        data={receivables}
        isLoading={isLoading}
        error={error}
        emptyTitle={t('emptyTitle')}
        emptyDescription={(hasFilters) =>
          hasFilters
            ? t('emptyDescriptionWithFilters')
            : t('emptyDescription')
        }
        emptyActionLabel={t('emptyActionLabel')}
        emptyActionOnClick={() => navigate('/receivables/new')}
        hasFilters={Boolean(search || statusFilter !== 'all')}
        errorMessage={`${t('errorLoadingReceivables')}: ${error?.message}`}
        columns={[
          {
            header: t('currency'),
            cell: (item) => <span className="font-medium">{item.currency}</span>,
          },
          {
            header: t('store'),
            cell: (item) => item.store,
          },
          {
            header: t('status'),
            cell: (item) => <ReceivableStatusBadge status={item.status} />,
          },
          {
            header: t('grossValue'),
            cell: (item) => (
                        <Badge className="bg-muted-foreground/10 text-muted-foreground font-mono" variant="outline">
                          {item.currency === 'BRL' ? 'R$' : item.currency === 'USD' ? 'US$' : '€'} {Number(item.grossValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </Badge>
            ),
          },
          {
            header: t('referenceMonth'),
            cell: (item) => formatDate(item.relatedDate),
          },
          {
            header: t('paidDate'),
            cell: (item) => item.paidDate ? formatDate(item.paidDate) : <Badge variant="secondary">—</Badge>,
          },
          {
            header: t('actions'),
            cell: (item) => (
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(item.id)} title={t('edit')}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} title={t('delete')}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
            ),
            className: "text-right",
          },
        ]}
      />

      {/* Pagination */}
      {!isLoading && !error && totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          showPageSizeOptions
          pageSizeOptions={[10, 20, 50]}
          onPageSizeChange={(size) => { setCurrentPage(1); setPageSize(size); }}
          showTotalItems
          syncWithQueryParams={false}
        />
      )}
      <ReceivableDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        receivableId={receivableToDelete?.id || ''}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};
